import React from 'react'
import {RefreshControl, View} from 'react-native'
import {Trans} from '@lingui/macro'
import {type NativeStackScreenProps} from '@react-navigation/native-stack'

import {NEWS_AUTHOR_HANDLE, NEWS_REQUIRED_TAG} from '#/lib/news/config'
import {NEWS_CATEGORIES} from '#/lib/news/newsCategories'
import {
  fetchTellusArticlePreviews,
  groupArticlesByCategory,
} from '#/lib/news/newsData'
import {type CommonNavigatorParams} from '#/lib/routes/types'
import {displayAtproto} from '#/lib/strings/identifiers'
import {useAgent} from '#/state/session'
import {atoms as a, useBreakpoints, useTheme} from '#/alf'
import * as Grid from '#/components/Grid'
import * as Layout from '#/components/Layout'
import {NewsCard} from '#/components/news/NewsCard'
import {Text} from '#/components/Typography'
import {type ArticlePreview} from '#/features/news/types'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'NewsCategories'>

export default function NewsCategoriesScreenOrModal({route}: Props) {
  const t = useTheme()
  const {gtMobile} = useBreakpoints()
  const agent = useAgent()
  const {categoryId} = route.params
  const topCategoryId = NEWS_CATEGORIES[0]?.id ?? 'top'
  const category =
    NEWS_CATEGORIES.find(item => item.id === categoryId) ?? NEWS_CATEGORIES[0]
  const [articles, setArticles] = React.useState<ArticlePreview[]>([])
  const [isRefreshing, setIsRefreshing] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(true)

  const refreshCategory = React.useCallback(async () => {
    setIsRefreshing(true)
    try {
      const previews = await fetchTellusArticlePreviews(
        agent,
        NEWS_CATEGORIES,
        topCategoryId,
      )
      const grouped = groupArticlesByCategory(previews, topCategoryId)
      setArticles(grouped[category?.id ?? topCategoryId] ?? [])
    } catch {
      setArticles([])
    } finally {
      setIsRefreshing(false)
      setIsLoading(false)
    }
  }, [agent, category?.id, topCategoryId])

  React.useEffect(() => {
    setIsLoading(true)
    refreshCategory()
  }, [refreshCategory])

  const columns = gtMobile ? 2 : 1
  const columnWidth = 1 / columns
  const rows = []
  for (let i = 0; i < articles.length; i += columns) {
    rows.push(articles.slice(i, i + columns))
  }

  return (
    <Layout.Screen testID="newsCategoryScreen">
      <Layout.Header.Outer>
        <Layout.Header.BackButton />
        <Layout.Header.Content>
          <Layout.Header.TitleText>
            {category?.title ?? 'Category'}
          </Layout.Header.TitleText>
        </Layout.Header.Content>
        <Layout.Header.Slot />
      </Layout.Header.Outer>

      <Layout.Content
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={refreshCategory}
          />
        }
        centerStyle={{maxWidth: 820}}
        contentContainerStyle={[a.pb_xl]}>
        <View style={[a.pt_md, a.px_lg, a.gap_lg]}>
          {isLoading ? (
            <View style={[a.py_xl, a.align_center]}>
              <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
                <Trans>Loading…</Trans>
              </Text>
            </View>
          ) : articles.length === 0 ? (
            <View style={[a.py_xl, a.align_center]}>
              <Text style={[a.text_md, a.font_bold]}>
                {category?.title ?? 'Category'}
              </Text>
              <Text
                style={[
                  a.text_sm,
                  a.pt_sm,
                  a.text_center,
                  t.atoms.text_contrast_medium,
                ]}>
                <Trans>
                  Post a thread from {displayAtproto(NEWS_AUTHOR_HANDLE)} with{' '}
                  {NEWS_REQUIRED_TAG} to publish here.
                </Trans>
              </Text>
            </View>
          ) : (
            <View style={[a.gap_md]}>
              {rows.map((row, rowIndex) => (
                <Grid.Row key={`category-row-${rowIndex}`} gap={a.gap_md.gap}>
                  {row.map(article => (
                    <Grid.Col
                      key={article.uri}
                      width={row.length === 1 ? 1 : columnWidth}>
                      <NewsCard
                        article={article}
                        categoryTitle={category?.title}
                        to={{
                          screen: 'NewsArticle',
                          params: {
                            uri: article.uri,
                          },
                        }}
                      />
                    </Grid.Col>
                  ))}
                </Grid.Row>
              ))}
            </View>
          )}
        </View>
      </Layout.Content>
    </Layout.Screen>
  )
}
